import { useMemo, useState } from "preact/hooks";
import { captureLead } from "../app/auth";
import { loadLeadSession, saveLeadSession, type LeadSession } from "../app/storage";
import { track } from "../app/analytics";
import { t } from "../i18n";

interface LeadCaptureProps {
  tool: "radar" | "skills-match";
  discipline?: string;
  interest?: string;
  title?: string;
  body?: string;
  onComplete: (lead: LeadSession) => void;
}

function utmPayload(): Record<string, string> {
  const params = new URLSearchParams(window.location.search);
  const out: Record<string, string> = {};
  for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]) {
    const value = params.get(key);
    if (value) out[key] = value.slice(0, 160);
  }
  return out;
}

export function LeadCapture({ tool, discipline, interest, title, body, onComplete }: LeadCaptureProps) {
  const existing = useMemo(loadLeadSession, []);
  const [email, setEmail] = useState(existing?.email ?? "");
  const [privacyConsent, setPrivacyConsent] = useState(Boolean(existing));
  const [marketingConsent, setMarketingConsent] = useState(existing?.marketingConsent ?? false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!privacyConsent) {
      setError(t("lead.error.consent"));
      return;
    }
    const normalized = email.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(normalized)) {
      setError(t("lead.error.email"));
      return;
    }
    setBusy(true);
    try {
      await captureLead({
        email: normalized,
        tool,
        discipline,
        interest,
        privacyConsent,
        marketingConsent,
        sourceUrl: window.location.href.slice(0, 1000),
        utm: utmPayload(),
      });
      const lead: LeadSession = {
        email: normalized,
        marketingConsent,
        capturedAt: new Date().toISOString(),
      };
      saveLeadSession(lead);
      track("email_captured", { tool, marketing: marketingConsent });
      onComplete(lead);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("lead.error.save"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section class="obs-flow-screen obs-lead-screen" aria-labelledby={`lead-${tool}-title`}>
      <div class="obs-flow-screen__inner">
        <p class="obs-flow-step">{t("lead.step")}</p>
        <h2 id={`lead-${tool}-title`}>{title ?? t("lead.continueTitle")}</h2>
        <p class="obs-flow-copy">
          {body ?? t("lead.continueBody")}
        </p>
        <form
          class="obs-lead-form"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <label class="obs-lead-email">
            <span>{t("lead.email")}</span>
            <input
              type="email"
              name="email"
              autocomplete="email"
              inputMode="email"
              required
              value={email}
              onInput={(event) => setEmail((event.target as HTMLInputElement).value)}
              placeholder={t("lead.placeholder")}
            />
          </label>
          <label class="obs-consent">
            <input
              type="checkbox"
              checked={privacyConsent}
              onChange={(event) => setPrivacyConsent((event.target as HTMLInputElement).checked)}
            />
            <span>
              {t("lead.privacy").replace("privacy policy.", "").trim()}{" "}
              <a href={window.BOOSTSCIENCE_OBSERVATORY?.privacyUrl ?? "/privacy-policy-2/"} target="_blank" rel="noreferrer">
                privacy policy
              </a>.
            </span>
          </label>
          <label class="obs-consent obs-consent--optional">
            <input
              type="checkbox"
              checked={marketingConsent}
              onChange={(event) => setMarketingConsent((event.target as HTMLInputElement).checked)}
            />
            <span>{t("lead.marketing")}</span>
          </label>
          {error ? <p class="obs-form-error" role="alert">{error}</p> : null}
          <button class="obs-btn obs-btn-primary obs-lead-submit" type="submit" disabled={busy}>
            {busy ? t("lead.saving") : t("match.continue")}
          </button>
        </form>
        <p class="obs-lead-note">{t("lead.note")}</p>
      </div>
    </section>
  );
}
